import React from 'react';
import { FiCheckCircle, FiArrowRight, FiVideo, FiTrendingUp, FiHash, FiUsers, FiMusic, FiAward } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { imageConfig } from '../../config/images';

const TikTokMarketingPage = () => {
  const features = [
    {
      icon: FiVideo,
      title: "Content Creation",
      description: "We create fun, engaging TikTok videos that tell your brand's story in a way people actually want to watch"
    },
    {
      icon: FiTrendingUp,
      title: "Trend-Based Ads",
      description: "We jump on trending sounds, challenges, and formats so your ads feel native to the platform—not intrusive"
    },
    {
      icon: FiHash,
      title: "Audience Targeting & Setup",
      description: "We figure out who your ideal customer is and make sure your ads reach the right people at the right time"
    },
    {
      icon: FiMusic,
      title: "Campaign Tracking & Growth",
      description: "We monitor your ad performance daily, adjust what's not working, and scale what's driving real results"
    }
  ];

  const pricingPlans = [
    {
      name: "Basic",
      price: "₹15,000",
      period: "/month",
      description: "Perfect for businesses starting on TikTok",
      features: [
        "12 Videos per Month",
        "Content Calendar",
        "Trend Research",
        "Basic Video Editing",
        "Hashtag Strategy",
        "Monthly Analytics Report",
        "Email Support"
      ],
      highlighted: false
    },
    {
      name: "Professional",
      price: "₹30,000",
      period: "/month",
      description: "Ideal for growing your TikTok presence and reach",
      features: [
        "20 Videos per Month",
        "Professional Video Production",
        "Viral Strategy Development",
        "TikTok Ads Management",
        "Influencer Outreach",
        "Bi-weekly Performance Reports",
        "Phone + Email Support"
      ],
      highlighted: true
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      description: "For brands needing full TikTok marketing solutions",
      features: [
        "Unlimited Videos",
        "Premium Video Production",
        "Advanced Ad Campaigns",
        "Influencer Campaign Management",
        "Hashtag Challenge Creation",
        "Branded Effects Development",
        "Weekly Strategy Calls",
        "Dedicated Account Manager"
      ],
      highlighted: false
    }
  ];

  const whyChooseUs = [
    {
      title: "We create videos people actually want to watch",
      description: "Not boring corporate ads—real, relatable content that fits TikTok's vibe and keeps people hooked"
    },
    {
      title: "We use authentic, not fake, TikTok video promotion",
      description: "We don't just throw money at ads; we combine smart targeting with creative content that people engage with"
    },
    {
      title: "You'll see results—followers, leads, and sales",
      description: "We focus on what matters: growing your audience and turning views into actual business outcomes"
    }
  ];

  const faqs = [
    {
      question: "Why should our business be on TikTok?",
      answer: "TikTok has over 1 billion active users with exceptional engagement rates, particularly among Gen Z and Millennials. The platform's algorithm gives even new accounts viral potential, unlike other platforms. TikTok users spend 95 minutes daily on the app, and brands see 3-5x higher engagement rates compared to Instagram or Facebook."
    },
    {
      question: "How do you create videos that go viral on TikTok?",
      answer: "Viral success combines trending sounds, relatable hooks in first 3 seconds, authentic content (not overly polished), participation in trends early, strategic hashtags, and optimal posting times. We analyze what's trending daily, understand your audience, and create content that balances trend participation with brand messaging for maximum viral potential."
    },
    {
      question: "What type of content works best on TikTok?",
      answer: "TikTok favors authentic, entertaining, and educational content. Best formats include behind-the-scenes, how-tos, challenges, duets, stitches, trending sounds, storytelling, and humor. The key is being genuinely engaging, not overly promotional. We create content that entertains first while subtly showcasing your brand."
    },
    {
      question: "Do we need to hire influencers or can we grow organically?",
      answer: "Both strategies work! TikTok's algorithm is more democratic than other platforms—organic growth is very achievable with consistent, quality content. However, influencer collaborations accelerate growth and credibility. We recommend a hybrid approach: strong organic strategy complemented by strategic influencer partnerships for campaigns."
    },
    {
      question: "How much do TikTok ads cost and are they effective?",
      answer: "TikTok ads typically cost ₹6-₹10 per CPM (1000 impressions) with minimum daily spend of ₹500. We recommend ₹30,000-₹50,000 monthly ad budget to test campaigns. TikTok ads are highly effective—users are 1.5x more likely to purchase products discovered on TikTok compared to other platforms due to high engagement."
    },
    {
      question: "What's the time commitment for managing TikTok?",
      answer: "TikTok requires consistent posting—ideally 3-5 videos weekly for algorithm favor. This is why outsourcing makes sense. We handle everything: content ideation, scripting, filming/editing, posting at optimal times, trend monitoring, and community engagement so you can focus on your business."
    },
    {
      question: "Can B2B companies succeed on TikTok?",
      answer: "Absolutely! TikTok isn't just for B2C. B2B companies like HubSpot, Shopify, and Adobe have massive TikTok success. The key is educational content, industry insights, workplace culture, and thought leadership delivered in TikTok's authentic, entertaining style. We've helped B2B clients reach decision-makers who are active on TikTok."
    },
    {
      question: "How do you measure TikTok marketing success?",
      answer: "We track views, engagement rate, follower growth, average watch time, shares, profile visits, and link clicks. More importantly, we measure business outcomes—website traffic from TikTok, lead generation, and sales attributed to TikTok campaigns using UTM tracking and conversion pixels."
    }
  ];

  const [openFaqIndex, setOpenFaqIndex] = React.useState<number | null>(null);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#000000] via-[#ff0050] to-[#00f2ea] text-white py-20">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              TikTok Advertising
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-100">
              TikTok Advertising helps your brand connect with people through short videos, trending formats, and creative storytelling. We create TikTok video promotions that actually work
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#pricing"
                className="bg-white text-[#000000] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
              >
                View Pricing
              </a>
              <a
                href="#contact"
                className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#000000] transition-colors inline-block"
              >
                Get Free Strategy
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What is Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-8 text-gray-900">
              What is TikTok Advertising?
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="text-lg leading-relaxed mb-6">
                TikTok advertising gives you a simple and effective way to reach your ideal audience where they're already spending time—watching entertaining videos. Instead of complicated jargon, think of it this way: TikTok ads help you show your brand to people who are most likely to care about what you offer. As a TikTok ads management agency, we handle all the setup, targeting, budgets, and creative so you can focus on running your business while we get you real results.
              </p>
              <p className="text-lg leading-relaxed">
                Whether you're just getting started or want to grow bigger, TikTok advertising works because it meets people where they are—entertained and engaged. We don't just run ads; we create video content that fits naturally into the TikTok experience so people actually watch, engage, and remember your brand. Real people, real results, no fluff.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Our TikTok Marketing Services
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow"
                >
                  <div className="w-12 h-12 bg-gradient-to-r from-[#000000] via-[#ff0050] to-[#00f2ea] bg-opacity-10 rounded-lg flex items-center justify-center mb-4 text-[#ff0050]">
                    <IconComponent className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">
            Transparent Pricing
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Choose the perfect plan for your business needs. All plans include expert management and optimization.
          </p>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`rounded-2xl p-8 transform hover:scale-105 transition-all duration-300 ${
                  plan.highlighted
                    ? 'bg-gradient-to-br from-[#000000] via-[#00f2ea] to-[#ff0050] text-white shadow-2xl scale-105 border-4 border-[#00f2ea]'
                    : 'bg-white border-2 border-gray-200 shadow-lg hover:shadow-2xl'
                }`}
              >
                {plan.highlighted && (
                  <div className="text-center mb-4">
                    <span className="bg-white text-[#000000] px-4 py-1 rounded-full text-sm font-semibold">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                <h3
                  className={`text-2xl font-bold mb-2 ${
                    plan.highlighted ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {plan.name}
                </h3>
                <div className="mb-4">
                  <span
                    className={`text-4xl font-bold ${
                      plan.highlighted ? 'text-white' : 'text-[#ff0050]'
                    }`}
                  >
                    {plan.price}
                  </span>
                  <span
                    className={`text-lg ${
                      plan.highlighted ? 'text-gray-100' : 'text-gray-600'
                    }`}
                  >
                    {plan.period}
                  </span>
                </div>
                <p
                  className={`mb-6 ${
                    plan.highlighted ? 'text-gray-100' : 'text-gray-600'
                  }`}
                >
                  {plan.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <FiCheckCircle
                        className={`w-5 h-5 mr-2 flex-shrink-0 mt-0.5 ${
                          plan.highlighted ? 'text-white' : 'text-[#ff0050]'
                        }`}
                      />
                      <span
                        className={
                          plan.highlighted ? 'text-gray-100' : 'text-gray-700'
                        }
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className={`block text-center px-6 py-3 rounded-lg font-semibold transition-colors ${
                    plan.highlighted
                      ? 'bg-white text-[#000000] hover:bg-gray-100'
                      : 'bg-gradient-to-br from-[#ff0050] to-[#00f2ea] text-white hover:opacity-90'
                  }`}
                >
                  Get Started
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Why Choose Mirani Marketing for TikTok?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {whyChooseUs.map((item, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-gradient-to-r from-[#000000] via-[#ff0050] to-[#00f2ea] bg-opacity-10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                    <FiCheckCircle className="w-5 h-5 text-[#ff0050]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-gray-900">
                      {item.title}
                    </h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Frequently Asked Questions
          </h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors flex justify-between items-center"
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <span className="text-[#ff0050] text-2xl">
                    {openFaqIndex === index ? '−' : '+'}
                  </span>
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 py-4 bg-white">
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-16 bg-gradient-to-br from-[#000000] via-[#ff0050] to-[#00f2ea] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Ready to Go Viral on TikTok?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-100">
            Get a free TikTok strategy and discover how to reach millions through viral content
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="bg-white text-[#000000] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
            >
              Get Free Strategy
            </a>
            <a
              href="tel:+919876543210"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#000000] transition-colors inline-block"
            >
              Call: +91 98765 43210
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TikTokMarketingPage;

